/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
// @ts-expect-error - we do this because of Storybook
import 'swiper/css/bundle'
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import { hasAtLeast, isEmpty } from 'remeda'
import type { Swiper } from 'swiper'
import { SwiperSlide, type SwiperSlideProps } from 'swiper/react'
import { useEventListener } from 'usehooks-ts'
import type { Slide, SlideModel } from '@velasca/types'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { NavigationButton } from '../NavigationButton/NavigationButton'
import { ZoomButton } from '../ZoomButton/ZoomButton'
import { ZoomedImage } from '../ZoomedImage/ZoomedImage'
import { SketchfabSlide } from './3DModelSlide/SketchfabSlide'
import { MainSlider } from './MainSlider/MainSlider'
import {
  Container,
  FullWidthImage,
  LeftArrowContainer,
  RightArrowContainer,
  StyledThumbSlider,
  Video,
} from './MediaCarousel.styles'
import type { MediaCarouselProps } from './MediaCarousel.types'
import {
  Bullet,
  BulletSquared,
  Model,
  Polaroid,
  Thumbnail,
} from './Thumbnail/Thumbnail'

const renderThumbnails = (
  images: (Slide | SlideModel)[],
  slider: Swiper,
  hasBullets: boolean,
  bulletsType: 'squared' | 'rounded',
) => {
  const polaroidRotations = [-8, 4, -2, -5]

  if (isEmpty(images)) {
    return null
  }

  return images.map((img, index) => {
    if (img.id && hasBullets) {
      if (bulletsType === 'squared') {
        return (
          <SwiperSlide className="cerca" key={`bullet-${img.id}`}>
            <BulletSquared
              onClick={() => {
                slider.slideTo(index + 1)
              }}
            />
          </SwiperSlide>
        )
      }

      return (
        <SwiperSlide key={`bullet-${img.id}`}>
          <Bullet
            onClick={() => {
              slider.slideTo(index + 1)
            }}
          />
        </SwiperSlide>
      )
    }
    if (img.type === 'icon') {
      return img.src ? (
        <SwiperSlide key={img.src.split('?v=')[1]}>
          <Thumbnail
            imageAlt={img.alt ?? ''}
            imageSrc={setQueryParams(img.src, [
              { name: 'width', value: '200' },
            ])}
            onClick={() => {
              slider.slideTo(index + 1)
            }}
          />
        </SwiperSlide>
      ) : null
    }
    if (img.id && img.type === 'polaroid') {
      return img.src ? (
        <SwiperSlide key={img.src.split('?v=')[1]}>
          <Polaroid
            imageAlt={img.alt ?? ''}
            rotationDegrees={polaroidRotations.shift()}
            imageSrc={setQueryParams(img.src, [
              { name: 'width', value: '200' },
            ])}
            onClick={() => {
              slider.slideTo(index + 1)
            }}
          />
        </SwiperSlide>
      ) : null
    }
    if (img.id && img.type === 'bullet') {
      if (bulletsType === 'squared') {
        return (
          <SwiperSlide key={`bullet-${img.id}`}>
            <BulletSquared
              onClick={() => {
                slider.slideTo(index + 1)
              }}
            />
          </SwiperSlide>
        )
      }

      return (
        <SwiperSlide key={`bullet-${img.id}`}>
          <Bullet
            onClick={() => {
              slider.slideTo(index + 1)
            }}
          />
        </SwiperSlide>
      )
    }
    if (img.id && img.type === 'model') {
      return (
        <SwiperSlide key={`bullet-${img.id}`}>
          <Model
            onClick={() => {
              slider.slideTo(index + 1)
            }}
          />
        </SwiperSlide>
      )
    }

    return null
  })
}

export const MediaCarousel = ({
  media,
  hasZoom,
  hasArrows = true,
  bulletsType = 'rounded',
  bulletsPosition = 'bottom',
  aspectRatio,
  forceBullets = false,
}: MediaCarouselProps): JSX.Element => {
  const [slider, setSlider] = useState<Swiper | undefined>()
  const [thumbSlider, setThumbSlider] = useState<Swiper | undefined>()
  const [isZoomedImageVisible, setZoomedImageVisible] = useState(false)
  const [realIndex, setRealIndex] = useState(0)
  const hasMultipleImages =
    hasAtLeast(media.slides, 2) || hasAtLeast(media.thumbnails, 2)
  const vidRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!vidRef.current) {
      return
    }

    if (media.slides[realIndex].type === 'video') {
      vidRef.current.play().catch(handleError)
    } else {
      vidRef.current.pause()
    }
  }, [vidRef, realIndex, media.slides])

  const hasModel = useMemo(
    () => media.thumbnails.some((thumbnail) => thumbnail.type === 'model'),
    [media],
  )

  const navigate = (e: KeyboardEvent) => {
    switch (e.code) {
      case 'Escape': {
        setZoomedImageVisible(false)
        break
      }
      case 'ArrowRight': {
        !hasModel && slider?.slideNext()
        break
      }
      case 'ArrowLeft': {
        !hasModel && slider?.slidePrev()
        break
      }
      default: {
        return
      }
    }
  }

  useEventListener('keydown', navigate)

  // Slides and Thumbnails are rendered as array because Swiper
  // does not accept a single React Component. Also Swiper bases
  // on element position, see more at https://swiperjs.com/react#slots
  const renderSlides = useCallback(
    (items: (Slide | SlideModel)[]) => {
      return items.map((slide) => {
        if (slide.type === 'model') {
          return (
            <SwiperSlide key={slide.id} className="slide--stretch">
              {({ isActive, isNext, isPrev }) => {
                return (
                  <SketchfabSlide
                    slide={slide}
                    isActive={isActive}
                    isNext={isNext}
                    isPrev={isPrev}
                  />
                )
              }}
            </SwiperSlide>
          )
        }

        if (slide.type === 'video') {
          return (
            <SwiperSlide key={slide.id} className="slide--stretch">
              <Video
                loop
                muted
                autoPlay
                playsInline
                controls={false}
                controlsList="nodownload"
                ref={vidRef}
                onContextMenu={(e) => {
                  // needed to prevent video download on right click.
                  e.preventDefault()

                  return false
                }}
              >
                {slide.sources?.map(({ src, mimeType }) => (
                  <source key={src} src={src} type={mimeType} />
                ))}
              </Video>
            </SwiperSlide>
          )
        }

        if (slide.type === 'image') {
          return (
            <SwiperSlide
              key={slide.id}
              className="slide--stretch"
              onClick={() => {
                hasZoom && setZoomedImageVisible(!isZoomedImageVisible)
              }}
            >
              <FullWidthImage
                lazyload
                lqip
                aspectRatio={aspectRatio}
                sources={buildIntrinsicWidthSources({ src: slide.src })}
                alt={slide.alt}
                sizes="(max-width: 767px) 100vw, 60vw"
                draggable={false}
                imgSrc={setQueryParams(slide.src, [
                  { name: 'width', value: '20' },
                ])}
              />
            </SwiperSlide>
          )
        }

        return null
      })
    },
    [hasZoom, isZoomedImageVisible, aspectRatio],
  )
  const hasBullets = useMemo(
    () =>
      media.thumbnails.some(({ type }) => type === 'bullet') || forceBullets,
    [media, forceBullets],
  )

  const thumbnails = useMemo(() => {
    return slider
      ? renderThumbnails(media.thumbnails, slider, hasBullets, bulletsType)
      : null
  }, [media, slider, hasBullets, bulletsType])

  const slides = useMemo(
    () => renderSlides(media.slides),
    [media, renderSlides],
  )

  const activeSlide: HTMLImageElement | undefined = slider
    ? slider.slides[slider.activeIndex]?.querySelector('img')
    : undefined

  const slideWithRealIndex = media.slides[realIndex]

  return (
    <>
      {isZoomedImageVisible &&
      !isMobile &&
      !hasModel &&
      hasArrows &&
      hasMultipleImages ? (
        <LeftArrowContainer>
          <NavigationButton
            buttonType="prev"
            onClick={() => {
              slider?.slidePrev()
            }}
          />
        </LeftArrowContainer>
      ) : null}
      {isZoomedImageVisible &&
      !isMobile &&
      !hasModel &&
      hasArrows &&
      hasMultipleImages ? (
        <RightArrowContainer>
          <NavigationButton
            buttonType="next"
            onClick={() => {
              slider?.slideNext()
            }}
          />
        </RightArrowContainer>
      ) : null}
      <Container hasZoom={hasZoom}>
        <MainSlider
          slider={slider}
          thumbSlider={thumbSlider}
          hasArrows={hasArrows && hasMultipleImages}
          loop={hasMultipleImages}
          onSwiper={setSlider}
          onSlideChange={(swiper) => {
            setRealIndex(swiper.realIndex)
          }}
        >
          {slides}
        </MainSlider>
        {thumbnails && hasMultipleImages ? (
          <StyledThumbSlider
            className={`${bulletsType} ${bulletsPosition}`}
            hasBullets={hasBullets}
            bulletsType={bulletsType}
            bulletsPosition={bulletsPosition}
            hasModel={hasModel}
            onSliderInit={setThumbSlider}
          >
            {thumbnails as (FunctionComponent<SwiperSlideProps> | null)[]}
          </StyledThumbSlider>
        ) : null}
        {isZoomedImageVisible &&
        activeSlide &&
        slideWithRealIndex.type !== 'model' ? (
          <ZoomedImage
            zoomedImg={slideWithRealIndex}
            imgSrc={activeSlide.currentSrc}
            onClick={() => {
              setZoomedImageVisible(false)
            }}
          />
        ) : null}
        {Boolean(media.slides) &&
          slideWithRealIndex.type === 'image' &&
          Boolean(hasZoom) && (
            <ZoomButton
              action={isZoomedImageVisible ? 'close' : 'open'}
              onClick={() => {
                setZoomedImageVisible(!isZoomedImageVisible)
              }}
            />
          )}
      </Container>
    </>
  )
}
